import { Injectable, NgZone } from '@angular/core';
import { MessageAttachment, QnA } from '../../../modals/qna/qna.interface';
import { ConfigService } from '../../../../common/services/config/config.service';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ChatStateService {
  private initialState: QnA[] = [];

  private userQnaSubject = new BehaviorSubject<QnA[]>(this.initialState);
  private isQueryResponseReceivedSubject = new BehaviorSubject<Boolean>(true); 
  private isChatWindowFullScreenSubject = new BehaviorSubject<Boolean>(false); 
  private customerName = new BehaviorSubject<string>("");
  private isWelcomeScreen = new BehaviorSubject<boolean>(true);
  private customerWelcomeMessage = new BehaviorSubject<string>("");
  private agentName = new BehaviorSubject<string>("");
  usersQna$ = this.userQnaSubject.asObservable();
  isQueryResponseReceived$ = this.isQueryResponseReceivedSubject.asObservable(); 
  isChatWindowFullScreen$ = this.isChatWindowFullScreenSubject.asObservable();
  customerName$ = this.customerName.asObservable();
  isWelcomeScreen$ = this.isWelcomeScreen.asObservable();
  customerWelcomeMessage$ = this.customerWelcomeMessage.asObservable();
  agentName$ = this.agentName.asObservable();

  private getCurrentState(): QnA[] {
    return this.userQnaSubject.getValue();
  }

  constructor(
    private configService: ConfigService,
    private ngZone: NgZone
  ) { }

  setQueryResponseReceived(value : boolean) {
    this.isQueryResponseReceivedSubject.next(value); 
  }

  setChatWindowFullScreen(value : boolean) {
    this.isChatWindowFullScreenSubject.next(value); 
  }

  updateQueryResponse(qnaId: string, response: any, agent: string): void {
    const currentState = this.getCurrentState();
    const updatedState = currentState.map((entry) => {
      if (entry.id === qnaId) {
        return {
          ...entry,
          answer: {
            ...entry.answer,
            message: response,
            receivedFrom: agent,
            id: qnaId
          },
        };
      }
      return entry;
    });
    this.userQnaSubject.next(updatedState);
  }

  getUserQnaIndex(index: number): QnA {
    const currentState = this.getCurrentState();
    return currentState[index];
  }

  updateQueryResponseId(qnaId: string, responseId: string): void {
    const currentState = this.getCurrentState();
    const updatedState = currentState.map((entry) => {
      if (entry.id === qnaId) {
        return {
          ...entry,
          answer: {
            ...entry.answer,
            id: responseId,
          },
        };
      }
      return entry;
    });
    this.userQnaSubject.next(updatedState);
  }

  updateQuerySentimentResponse(qnaId: string, response: any): void {

    const currentState = this.getCurrentState();
    const updatedState = currentState.map((entry) => {
      if (entry.id === qnaId) {
        return {
          ...entry,
          question: {
            ...entry.question,
            score: response.data.score,
            label: response.data.label,
          },
        };
      }
      return entry;
    });
    this.userQnaSubject.next(updatedState);
  }

  updateQueryResponseStream(qnaId: string, newMessage: any): void {
    const currentState = this.getCurrentState();
    const updatedState = currentState.map((entry) => {
      if (entry.id === qnaId) {
        const receivedFrom =
          entry.answer.receivedFrom === "HumanAgent" ? "HumanAgent" : "VirtualAgent";
        return {
          ...entry,
          answer: {
            ...entry.answer,
            // Re-normalize the full string each chunk so late-closing **bold** converts once complete.
            message: this.normalizeAiFormatting((entry.answer.message || "") + newMessage),
            id: "dummyId",
            receivedFrom,
          },
        };
      }
      return entry;
    });
    this.userQnaSubject.next(updatedState);
  }

  /** Convert leaked Markdown bold to HTML the chat bubble already styles via [innerHTML]. */
  private normalizeAiFormatting(text: string): string {
    if (!text) {
      return text;
    }
    return text.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  }

  addInitialEmptyQuestionToUsersQna() {
    const qna : QnA = this.getQna();
    qna.answer.receivedFrom = "VirtualAgent";

    const currentState = this.getCurrentState();
    currentState.push(qna); 
    this.userQnaSubject.next(currentState);
  }

  appendAnswerToUsersQna(qnaId: string, answer: string) {
    const currentState = this.getCurrentState();
    const updatedState = currentState.map((entry) => {
      if (entry.id === qnaId) {
        return {
          ...entry,
          answer: {
            ...entry.answer,
            message: answer,
          },
        };
      }
      return entry;
    });
    this.userQnaSubject.next(updatedState);
  }

  addAgentMessageEmptyQuestion(message: string) {
    const qna : QnA = this.getQna(); 

    qna.answer.message = message; 
    qna.answer.id = "dummyAnswerId"; 
    qna.answer.receivedFrom = "VirtualAgent";

    const currentState = this.getCurrentState();
    currentState.push(qna); 
    this.userQnaSubject.next(currentState);
  }

  addStandaloneStreamingAgentMessage(message: string, receivedFrom: string, createdTime?: Date | string) {
    const qna: QnA = this.getQna();
    qna.answer.message = this.normalizeAiFormatting(message);
    qna.answer.id = "dummyId";
    qna.answer.receivedFrom = receivedFrom;
    if (createdTime) {
      qna.answer.createdTime = createdTime;
    }

    const currentState = this.getCurrentState();
    this.userQnaSubject.next([...currentState, qna]);
  }

  removeTrailingStandaloneAgentLoader(receivedFrom: string) {
    const currentState = this.getCurrentState();
    const lastEntry = currentState[currentState.length - 1];

    if (
      lastEntry &&
      lastEntry.question.message === "" &&
      lastEntry.answer.receivedFrom === receivedFrom &&
      lastEntry.answer.id === "" &&
      lastEntry.answer.message === ""
    ) {
      this.userQnaSubject.next(currentState.slice(0, -1));
    }
  }

  updateQueryType(qnaId: string, type: string) {
    const currentState = this.getCurrentState();
    const updatedState = currentState.map((entry) => {
      if (entry.id === qnaId) {
        return {
          ...entry,
          question: {
            ...entry.question,
            isAction: type,
          },
        };
      }
      return entry;
    });
    this.userQnaSubject.next(updatedState);
  }

  appendHumanAgentResponseAndAnswerId(qnaId: string, answer: string, answerId: string) {
    const currentState = this.getCurrentState();
    const updatedState = currentState.map((entry) => {
      if (entry.id === qnaId) {
        return {
          ...entry,
          answer: {
            ...entry.answer,
            message: answer,
            id: answerId
          },
        };
      }
      return entry;
    });
    this.userQnaSubject.next(updatedState);
  }

  addLikeDislikeToUserQna(qnaId: string, isLike: boolean) {
    const currentState = this.getCurrentState();
    const updatedState = currentState.map((entry) => {
      if (entry.id === qnaId) {
        return {
          ...entry,
          answer: {
            ...entry.answer,
            isLike,
            isDislike: !isLike,
          },
        };
      }
      return entry;
    });
    this.userQnaSubject.next(updatedState);
  }

  updateAnswerTicket(qnaId: string, ticketUrl: any): void {
    const currentState = this.getCurrentState();
    const updatedState = currentState.map((entry) => {
      if (entry.id === qnaId) {
        return {
          ...entry,
          answer: {
            ...entry.answer,
            ticketUrl,
            isTicketCreated: true,
          },
        };
      }
      return entry;
    });
    this.userQnaSubject.next(updatedState);
  }

  updateVirtualAgentSpeaking(qnaId: string, isSpeaking: boolean) {

    const currentState = this.getCurrentState();
    const updatedState = currentState.map((entry) => {
      if (entry.id == qnaId) {
        return {
          ...entry,
          answer: {
            ...entry.answer,
            isVirtualAgentSpeaking: isSpeaking
          }
        }
      }
      return entry;
    });
    this.userQnaSubject.next(updatedState);
  }

  updateVirtualAgentSpeakingPaused(qnaId: string, isSpeakingPaused: boolean) {
    const currentState = this.getCurrentState();
    const updatedState = currentState.map((entry) => {
      if (entry.id == qnaId) {
        return {
          ...entry,
          answer: {
            ...entry.answer,
            isVirtualAgentSpeakingPaused: isSpeakingPaused
          }
        }
      }
      return entry;
    });
    this.userQnaSubject.next(updatedState);
  }

  updateIdForMessageResponse(index: number, responseId: string) {
    const currentState = this.getCurrentState();
    if (index >= 0 && index < currentState.length) {
      const updatedEntry = {
        ...currentState[index],
        answer: {
          ...currentState[index].answer,
          id: responseId,
        },
      };
      const updatedState = [
        ...currentState.slice(0, index),
        updatedEntry,
        ...currentState.slice(index + 1),
      ];
      this.userQnaSubject.next(updatedState);
    } else {
      console.error("Index out of bounds");
    }
  }

  addEntryToUsersQna(qna: QnA) {
    const currentState = this.getCurrentState();
    this.userQnaSubject.next([...currentState, qna]);
  }

  removeQnaEntry(qnaId: string) {
    const currentState = this.getCurrentState();
    this.userQnaSubject.next(currentState.filter(entry => entry.id !== qnaId));
  }

  /**
   * Suppress an empty AI answer on an existing QnA without removing the user's question bubble.
   * Used when the LLM intentionally stays silent (e.g. after the request_user_feedback tool runs)
   * — keeps the user's message visible, drops the would-be empty AI bubble.
   */
  clearAnswerForQna(qnaId: string) {
    const currentState = this.getCurrentState();
    const updatedState = currentState.map((entry) => {
      if (entry.id !== qnaId) return entry;
      return {
        ...entry,
        answer: {
          ...entry.answer,
          id: "",
          message: "",
          receivedFrom: "",
        },
      };
    });
    this.userQnaSubject.next(updatedState);
  }

  clearUsersQna() {
    const defaultState: QnA[] = [
      {
        id: Math.random() + "",
        question: {
          message: "",
          score: 0,
          label: "neutral",
          isAction: "Information",
          createdTime: new Date()
        },
        answer: {
          id: "",
          receivedFrom: "",
          message: "Welcome, how can I help you?",
          isLike: false,
          isDislike: false,
          isTicketCreated: false,
          ticketUrl: "",
          isVirtualAgentSpeaking: false,
          isVirtualAgentSpeakingPaused: false,
          createdTime: new Date()
        },
      },
    ];
    this.userQnaSubject.next(defaultState);
  }

  getQna() {
    const currentQnA: QnA = {
      id: Math.random() + "",
      question: {
        message: "",
        score: 0,
        label: "neutral",
        isAction: "Information",
        createdTime: new Date()
      },
      answer: {
        id: "",
        receivedFrom: "",
        message: "",
        isLike: false,
        isDislike: false,
        isTicketCreated: false,
        ticketUrl: "",
        isVirtualAgentSpeaking: false,
        isVirtualAgentSpeakingPaused: false,
        createdTime: new Date()
      },
    };
    return currentQnA;
  }

  formatResponseMessage(message: string): string {
    let formattedMessage = message
      .replace(/\\+n/g, "<br>")
      .replace(/ {2,}/g, " ");

    formattedMessage = formattedMessage.replace(
      /(https?:\/\/[^\s]+)/g,
      '<a class="text-blue-700" href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
    );
    return formattedMessage;
  }

  private streamingIntervals = new Map<string, ReturnType<typeof setInterval>>();
  private streamingActiveIds = new Set<string>();
  private streamingElements = new Map<string, HTMLElement>();
  private streamingTargetText = new Map<string, string>();
  private streamingFinishCallbacks = new Map<string, () => void>();

  isActivelyStreaming(qnaId: string): boolean {
    return this.streamingActiveIds.has(qnaId);
  }

  /**
   * Returns true when the message for this qnaId is actively streaming AND the
   * full target text already contains a <table — meaning the skeleton loader
   * component should be shown in place of the incomplete table markup.
   */
  isStreamingTable(qnaId: string): boolean {
    if (!this.streamingActiveIds.has(qnaId)) return false;
    const target = this.streamingTargetText.get(qnaId) ?? '';
    return target.toLowerCase().includes('<table');
  }

  private readonly streamCharDelayMs = environment.streamResponseTypingSpeed;

  /** Trims a partially-streamed HTML string to avoid rendering half-open tags mid-stream. */
  private toSafePartialHtml(partial: string): string {
    const lastOpen = partial.lastIndexOf('<');
    if (lastOpen === -1) {
      return partial;
    }
    const hasClosing = partial.indexOf('>', lastOpen) !== -1;
    return hasClosing ? partial : partial.substring(0, lastOpen);
  }

  /**
   * Builds the HTML to show for a partially-streamed message.
   * Everything from the first <table onward is always suppressed during streaming
   * — whether the table is open or already closed in the partial text.
   * The complete, valid table is only written by _completeStreaming when the
   * stream finishes, ensuring the table never renders mid-stream.
   */
  private toStreamingPartialHtml(partial: string): string {
    const firstTableOpen = partial.toLowerCase().indexOf('<table');
    if (firstTableOpen === -1) {
      return this.toSafePartialHtml(partial);
    }
    // Strip the table (and anything after it) — show only pre-table text.
    return this.toSafePartialHtml(partial.substring(0, firstTableOpen));
  }

  smoothStreamResponse(text: string, element: HTMLElement, qnaId: string, scrollContainer?: HTMLElement) {
    this.streamingTargetText.set(qnaId, text);

    if (this.streamingIntervals.has(qnaId)) {
      return;
    }

    this.streamingActiveIds.add(qnaId);
    this.streamingElements.set(qnaId, element);
    element.classList.add('is-streaming');

    let displayed = 0;

    this.ngZone.runOutsideAngular(() => {
      const interval = setInterval(() => {
        const target = this.streamingTargetText.get(qnaId) || '';

        if (displayed < target.length) {
          displayed++;
          element.innerHTML = this.toStreamingPartialHtml(target.substring(0, displayed));
          if (scrollContainer) {
            scrollContainer.scrollTop = scrollContainer.scrollHeight;
          }
        }

        if (displayed >= target.length && this.streamingFinishCallbacks.has(qnaId)) {
          this._completeStreaming(qnaId, interval, element);
        }
      }, this.streamCharDelayMs);

      this.streamingIntervals.set(qnaId, interval);
    });
  }

  gracefullyFinishStreaming(qnaId: string, onComplete: () => void) {
    if (!this.streamingIntervals.has(qnaId)) {
      onComplete();
      return;
    }
    this.streamingFinishCallbacks.set(qnaId, onComplete);
  }

  private _completeStreaming(qnaId: string, interval: ReturnType<typeof setInterval>, element: HTMLElement) {
    const callback = this.streamingFinishCallbacks.get(qnaId);
    const finalText = this.streamingTargetText.get(qnaId) || '';
    this.streamingFinishCallbacks.delete(qnaId);
    clearInterval(interval);
    this.streamingIntervals.delete(qnaId);
    this.streamingElements.delete(qnaId);
    this.streamingTargetText.delete(qnaId);

    // Run both operations inside the zone so Angular processes them in a single
    // change detection cycle and renders the swap atomically — skeleton out,
    // real table in, in the same paint with no intermediate empty frame.
    this.ngZone.run(() => {
      this.streamingActiveIds.delete(qnaId);
      element.innerHTML = finalText;
      element.classList.remove('is-streaming');

      // Fade the final content in so the table doesn't appear abruptly when it
      // replaces the skeleton. Re-trigger the animation by toggling the class.
      element.classList.remove('stream-content-reveal');
      void element.offsetWidth; // force reflow so the animation restarts
      element.classList.add('stream-content-reveal');

      callback?.();
    });
  }

  stopStreamingForId(qnaId: string) {
    if (this.streamingIntervals.has(qnaId)) {
      clearInterval(this.streamingIntervals.get(qnaId));
      this.streamingIntervals.delete(qnaId);
    }
    const el = this.streamingElements.get(qnaId);
    if (el) el.classList.remove('is-streaming');
    this.streamingActiveIds.delete(qnaId);
    this.streamingElements.delete(qnaId);
    this.streamingTargetText.delete(qnaId);
    this.streamingFinishCallbacks.delete(qnaId);
  }

  typeWriter(text: string, element: HTMLElement, speed: number) {
    element.innerHTML = text;
  }

  clearStreamingIntervals() {
    this.streamingIntervals.forEach((interval) => clearInterval(interval));
    this.streamingIntervals.clear();
    this.streamingActiveIds.clear();
    this.streamingElements.clear();
    this.streamingTargetText.clear();
    this.streamingFinishCallbacks.clear();
  }

  setCustomerName(value: string): void {
    this.customerName.next(value);
  }

  setIsWelcomeScreen(value: boolean): void {
    this.isWelcomeScreen.next(value);
  }

  setCustomerWelcomeMessage(value: string): void {
    this.customerWelcomeMessage.next(value);
  }

  setAgentName(value: string): void{
    this.agentName.next(value);
  }

  /**
   * When a rich AI message arrives over realtime while the chat stream is still open (or before
   * {@code stop:true}), the VirtualAgent loader row for the current user question can remain visible
   * above the new rich bubble. Clears that pending answer shell and stops smooth-stream timers.
   */
  suppressVirtualAgentLoaderWhenRichMessageArrives(): void {
    const currentState = this.getCurrentState();
    const pendingIds: string[] = [];
    const updatedState = currentState.map((entry) => {
      const isPendingLoader =
        !!entry.question.message?.trim() &&
        entry.answer.receivedFrom === "VirtualAgent" &&
        (entry.answer.id === "" || entry.answer.id === "dummyId") &&
        !entry.answer.attachment &&
        !entry.answer.message?.trim();
      if (!isPendingLoader) {
        return entry;
      }
      pendingIds.push(entry.id);
      return {
        ...entry,
        answer: {
          ...entry.answer,
          id: "",
          message: "",
          // Keep VirtualAgent so later stream chunks on this QnA still render as AI Service.
          receivedFrom: "VirtualAgent",
        },
      };
    });
    pendingIds.forEach((id) => this.stopStreamingForId(id));
    this.userQnaSubject.next(updatedState);
  }

  /**
   * Append an AI bubble that carries an interactive widget (e.g. rating).
   * Idempotent on the same {@code attachment.messageId} so duplicate STOMP arrivals
   * don't render the bubble twice.
   */
  appendRichAiMessage(message: string, attachment: MessageAttachment): string {
    const currentState = this.getCurrentState();
    const existing = currentState.find(
      (entry) => entry.answer.attachment?.messageId === attachment.messageId
    );
    if (existing) {
      return existing.id;
    }

    const qna: QnA = this.getQna();
    qna.answer.message = message ?? "";
    qna.answer.id = "ANSWER_id";
    qna.answer.receivedFrom = "VirtualAgent";
    qna.answer.attachment = attachment;
    this.userQnaSubject.next([...currentState, qna]);
    return qna.id;
  }

  /**
   * Patch the {@code state} of an existing rich message's attachment (e.g. mark a rating
   * submitted with the chosen value). The widget reads {@code state} via inputs and
   * re-renders accordingly.
   */
  updateAttachmentState(
    qnaId: string,
    state: Record<string, any>
  ): void {
    const currentState = this.getCurrentState();
    const updatedState = currentState.map((entry) => {
      if (entry.id === qnaId && entry.answer.attachment) {
        return {
          ...entry,
          answer: {
            ...entry.answer,
            attachment: {
              ...entry.answer.attachment,
              state: { ...(entry.answer.attachment.state ?? {}), ...state },
            },
          },
        };
      }
      return entry;
    });
    this.userQnaSubject.next(updatedState);
  }
}
